import React, { useEffect, useRef, useState } from 'react';
import Button from '../ui/Button';
import './list.css';

/**
 * Per-column filter menu, in the spirit of the grid this table replaced: pick an
 * operator, type a value, and the server narrows the list.
 *
 * Filtering has to happen server-side. These lists are paginated, so filtering the
 * rows already on screen would only ever search 25 of 38,000 users.
 */

export const FILTER_OPS = {
  text: [
    { value: 'contains', label: 'Contains' },
    { value: 'notcontains', label: 'Does not contain' },
    { value: 'eq', label: 'Equals' },
    { value: 'startswith', label: 'Starts with' },
    { value: 'endswith', label: 'Ends with' },
    { value: 'empty', label: 'Is empty' },
    { value: 'notempty', label: 'Is not empty' },
  ],
  number: [
    { value: 'gte', label: 'At least (≥)' },
    { value: 'lte', label: 'At most (≤)' },
    { value: 'gt', label: 'More than (>)' },
    { value: 'lt', label: 'Less than (<)' },
    { value: 'eq', label: 'Equals (=)' },
    { value: 'ne', label: 'Not equal (≠)' },
  ],
  date: [
    { value: 'eq', label: 'On' },
    { value: 'gte', label: 'On or after' },
    { value: 'lte', label: 'On or before' },
  ],
};

/** Operators that are complete on their own -- no value box, apply immediately. */
export const VALUELESS_OPS = new Set(['empty', 'notempty']);

const DEFAULT_OP = { text: 'contains', number: 'gte', date: 'gte' };

/** "≥ 5", "Contains gmail" -- what a column's header shows once it is filtered. */
export function describeFilter(kind, filter) {
  if (!filter) return '';
  const op = (FILTER_OPS[kind] || []).find((o) => o.value === filter.op);
  const label = op?.label || filter.op;
  return VALUELESS_OPS.has(filter.op) ? label : `${label} ${filter.value}`;
}

export default function ColumnFilter({ column, kind = 'text', value, onApply }) {
  const [open, setOpen] = useState(false);
  const [op, setOp] = useState(value?.op || DEFAULT_OP[kind] || 'contains');
  const [text, setText] = useState(value?.value || '');
  const wrapRef = useRef(null);
  const inputRef = useRef(null);

  // Reopening should show what is actually applied, not whatever was last typed and
  // abandoned.
  useEffect(() => {
    if (open) {
      setOp(value?.op || DEFAULT_OP[kind] || 'contains');
      setText(value?.value || '');
    }
  }, [open, value, kind]);

  useEffect(() => {
    if (!open) return undefined;
    const onDocClick = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('keydown', onKey);
    inputRef.current?.focus();
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const ops = FILTER_OPS[kind] || FILTER_OPS.text;
  const needsValue = !VALUELESS_OPS.has(op);

  const apply = () => {
    if (needsValue && String(text).trim() === '') return;
    onApply(column, { op, value: needsValue ? String(text).trim() : '' });
    setOpen(false);
  };

  const clear = () => {
    onApply(column, null);
    setOpen(false);
  };

  return (
    <span className="sv-colfilter" ref={wrapRef}>
      <button
        type="button"
        className={`sv-colfilter__btn ${value ? 'is-active' : ''}`}
        aria-label={value ? `Filter on ${column}: ${describeFilter(kind, value)}` : `Filter ${column}`}
        aria-expanded={open}
        title={value ? describeFilter(kind, value) : 'Filter'}
        onClick={(e) => {
          // The header cell itself toggles sorting.
          e.stopPropagation();
          setOpen((v) => !v);
        }}
      >
        <i className={`bx ${value ? 'bxs-filter-alt' : 'bx-filter-alt'}`} aria-hidden="true" />
      </button>

      {open && (
        <div
          className="sv-colfilter__pop"
          role="dialog"
          onClick={(e) => e.stopPropagation()}
        >
          <select
            className="sv-colfilter__op"
            value={op}
            onChange={(e) => setOp(e.target.value)}
            aria-label="Filter operator"
          >
            {ops.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>

          {needsValue && (
            <input
              ref={inputRef}
              className="sv-colfilter__value"
              type={kind === 'number' ? 'number' : kind === 'date' ? 'date' : 'text'}
              value={text}
              placeholder="Value"
              onChange={(e) => setText(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') apply();
              }}
              aria-label="Filter value"
            />
          )}

          <div className="sv-colfilter__actions">
            <Button size="sm" onClick={apply}>
              Apply
            </Button>
            {value && (
              <Button size="sm" variant="ghost" onClick={clear}>
                Clear
              </Button>
            )}
          </div>
        </div>
      )}
    </span>
  );
}
